import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  MedicalServices as MedicalIcon
} from '@mui/icons-material';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { surgicalProcedureMappingService } from '../../services/surgicalProcedureMappingService';
import { surgicalEquipmentService } from '../../services/surgicalEquipmentService';
import EquipmentMultiSelect from '../common/EquipmentMultiSelect';
import SimpleProcedureCodeAutocomplete from '../common/SimpleProcedureCodeAutocomplete';
import { useUserData } from '../../hooks/useUserData';

// Helper function to normalize equipment needed for backward compatibility
const normalizeEquipmentNeeded = (equipmentNeeded) => {
  if (!equipmentNeeded) return [];
  if (Array.isArray(equipmentNeeded)) return equipmentNeeded;
  // Handle legacy string format - convert to empty array for now
  // In a real migration, you might want to parse or convert existing string data
  return [];
};

const SurgicalProcedureMapping = () => {
  const { defaultFacility } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  // Get default facility ID - prioritize user's defaultFacility, then first userFacility
  const defaultFacilityId = defaultFacility?.id || '';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [procedureMappings, setProcedureMappings] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [equipmentMap, setEquipmentMap] = useState({});

  // Body systems for dropdown
  const bodySystems = [
    'Cardiovascular System',
    'Respiratory System',
    'Digestive System', 
    'Nervous System',
    'Musculoskeletal System',
    'Genitourinary System',
    'Endocrine System',
    'Integumentary System',
    'Lymphatic System',
    'Reproductive System'
  ];

  // Approaches for dropdown
  const approaches = [
    'Open',
    'Minimally Invasive',
    'Laparoscopic',
    'Endoscopic',
    'Percutaneous',
    'Robotic',
    'Microsurgical',
    'Arthroscopic'
  ];

  const loadEquipmentMap = useCallback(async () => {
    if (!defaultFacilityId) {
      setEquipmentMap({});
      return;
    }
    
    try {
      const equipment = await surgicalEquipmentService.getAllEquipment(defaultFacilityId);
      const equipmentMapping = {};
      equipment.forEach(item => {
        equipmentMapping[item.id] = item;
      });
      setEquipmentMap(equipmentMapping);
    } catch (err) {
      console.error('Error loading equipment map:', err);
      setEquipmentMap({});
    }
  }, [defaultFacilityId]);

  const loadProcedureMappings = useCallback(async () => {
    if (!defaultFacilityId) {
      setProcedureMappings([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const mappings = await surgicalProcedureMappingService.getAllProcedureMappings(defaultFacilityId);
      setProcedureMappings(mappings);
      // Also load equipment map for proper display
      await loadEquipmentMap();
    } catch (err) {
      setError('Failed to load procedure mappings: ' + err.message);
      console.error('Error loading procedure mappings:', err);
    } finally {
      setLoading(false);
    }
  }, [defaultFacilityId, loadEquipmentMap]);

  // Load procedure mappings when facility changes
  useEffect(() => {
    loadProcedureMappings();
  }, [loadProcedureMappings]);

  const handleEdit = (mapping) => {
    setEditingRow(mapping.id);
    setEditingData({
      cptCode: mapping.cptCode,
      cptCodeObject: null, // Will be populated by the autocomplete if needed
      friendlyName: mapping.friendlyName,
      bodySystem: mapping.bodySystem,
      approach: mapping.approach,
      equipmentNeeded: normalizeEquipmentNeeded(mapping.equipmentNeeded),
      defaultDuration: mapping.defaultDuration
    });
  };

  const handleSave = async (mappingId) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Remove the cptCodeObject before saving (we only need the cptCode string)
      const { cptCodeObject, ...dataToSave } = editingData;
      await surgicalProcedureMappingService.updateProcedureMapping(mappingId, dataToSave);
      setSuccess('Procedure mapping updated successfully');
      setEditingRow(null);
      setEditingData({});
      await loadProcedureMappings();
    } catch (err) {
      setError('Failed to update procedure mapping: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditingData({});
  };

  const handleDelete = async (mappingId) => {
    if (window.confirm('Are you sure you want to delete this procedure mapping?')) {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        await surgicalProcedureMappingService.deleteProcedureMapping(mappingId);
        setSuccess('Procedure mapping deleted successfully');
        await loadProcedureMappings();
      } catch (err) {
        setError('Failed to delete procedure mapping: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddNew = () => {
    setEditingData({
      cptCode: '',
      cptCodeObject: null,
      friendlyName: '',
      bodySystem: '',
      approach: '',
      equipmentNeeded: [],
      defaultDuration: 60
    });
    setOpenDialog(true);
  };

  const handleCreateNew = async () => {
    if (!defaultFacilityId) {
      setError('No default facility available');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Remove the cptCodeObject before saving (we only need the cptCode string)
      const { cptCodeObject, ...dataToSave } = editingData;
      await surgicalProcedureMappingService.createProcedureMapping({
        ...dataToSave,
        facilityId: defaultFacilityId
      });
      setSuccess('Procedure mapping created successfully');
      setOpenDialog(false);
      setEditingData({});
      await loadProcedureMappings();
    } catch (err) {
      setError('Failed to create procedure mapping: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditingDataChange = (field, value) => {
    setEditingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search and filter
  const filteredMappings = procedureMappings.filter(mapping =>
    (mapping.cptCode && mapping.cptCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (mapping.friendlyName && mapping.friendlyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (mapping.bodySystem && mapping.bodySystem.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (mapping.approach && mapping.approach.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination
  const paginatedMappings = filteredMappings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (!defaultFacilityId && userFacilities.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No facilities available. Please ensure you have access to at least one facility.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Surgical Procedure Mapping
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          disabled={!defaultFacilityId}
        >
          Add New Mapping
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          label="Search procedures..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>CPT Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Friendly Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Body System</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Approach</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Equipment Needed</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Default Duration</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                            {paginatedMappings.map((mapping) => (
                <React.Fragment key={mapping.id}>
                  <TableRow hover>
                    <TableCell>
                      <Chip
                        label={mapping.cptCode}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {mapping.friendlyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.bodySystem}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.approach}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ maxWidth: 200 }}>
                        {(() => {
                          const normalizedEquipment = normalizeEquipmentNeeded(mapping.equipmentNeeded);
                          return normalizedEquipment.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {normalizedEquipment.slice(0, 2).map((equipmentId) => {
                                const equipment = equipmentMap[equipmentId];
                                return (
                                  <Chip
                                    key={equipmentId}
                                    label={equipment ? equipment.name : 'Unknown Equipment'}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    title={equipment ? `${equipment.name} (${equipment.type})` : 'Equipment details not available'}
                                  />
                                );
                              })}
                              {normalizedEquipment.length > 2 && (
                                <Chip
                                  label={`+${normalizedEquipment.length - 2} more`}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  title={`Total ${normalizedEquipment.length} equipment items selected`}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No equipment specified
                            </Typography>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDuration(mapping.defaultDuration)}
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEdit(mapping)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDelete(mapping.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expandable Edit Form Row */}
                  {editingRow === mapping.id && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none' }}>
                        <Box sx={{ 
                          py: 3, 
                          px: 2, 
                          backgroundColor: '#f8f9fa',
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          mb: 1
                        }}>
                          <Typography variant="h6" sx={{ mb: 3, color: '#1976d2', fontWeight: 600 }}>
                            Edit Procedure Mapping
                          </Typography>
                          
                          <Box component="form" sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr' }}>
                            {/* Left Column */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <SimpleProcedureCodeAutocomplete
                                codeType="CPT4"
                                value={editingData.cptCodeObject}
                                onChange={(codeObject) => {
                                  handleEditingDataChange('cptCode', codeObject?.procedure_code || '');
                                  handleEditingDataChange('cptCodeObject', codeObject);
                                }}
                                fullWidth
                                label="CPT Code"
                              />
                              
                              <TextField
                                label="Friendly Name"
                                value={editingData.friendlyName}
                                onChange={(e) => handleEditingDataChange('friendlyName', e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                              />
                              
                              <TextField
                                label="Default Duration (minutes)"
                                type="number"
                                value={editingData.defaultDuration}
                                onChange={(e) => handleEditingDataChange('defaultDuration', e.target.value)}
                                fullWidth
                              />
                            </Box>
                            
                            {/* Right Column */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <FormControl fullWidth>
                                <InputLabel>Body System</InputLabel>
                                <Select
                                  value={editingData.bodySystem}
                                  onChange={(e) => handleEditingDataChange('bodySystem', e.target.value)}
                                  label="Body System"
                                >
                                  {bodySystems.map(system => (
                                    <MenuItem key={system} value={system}>{system}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <FormControl fullWidth>
                                <InputLabel>Approach</InputLabel>
                                <Select
                                  value={editingData.approach}
                                  onChange={(e) => handleEditingDataChange('approach', e.target.value)}
                                  label="Approach"
                                >
                                  {approaches.map(approach => (
                                    <MenuItem key={approach} value={approach}>{approach}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              
                              <EquipmentMultiSelect
                                facilityId={defaultFacilityId}
                                value={editingData.equipmentNeeded || []}
                                onChange={(selectedIds) => handleEditingDataChange('equipmentNeeded', selectedIds)}
                                fullWidth
                                label="Equipment Needed"
                              />
                            </Box>
                          </Box>
                          
                          {/* Action Buttons */}
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                              variant="outlined"
                              onClick={handleCancel}
                              startIcon={<CancelIcon />}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleSave(mapping.id)}
                              startIcon={<SaveIcon />}
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Box>
                        </Box>
                                             </TableCell>
                     </TableRow>
                   )}
                 </React.Fragment>
              ))}
              {paginatedMappings.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <MedicalIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No procedure mappings found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add your first procedure mapping to get started
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredMappings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add New Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Procedure Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <SimpleProcedureCodeAutocomplete
              codeType="CPT4"
              value={editingData.cptCodeObject}
              onChange={(codeObject) => {
                handleEditingDataChange('cptCode', codeObject?.procedure_code || '');
                handleEditingDataChange('cptCodeObject', codeObject);
              }}
              required
              fullWidth
              label="CPT Code"
            />
            <TextField
              label="Friendly Name"
              value={editingData.friendlyName}
              onChange={(e) => handleEditingDataChange('friendlyName', e.target.value)}
              required
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Body System</InputLabel>
              <Select
                value={editingData.bodySystem}
                onChange={(e) => handleEditingDataChange('bodySystem', e.target.value)}
                label="Body System"
              >
                {bodySystems.map(system => (
                  <MenuItem key={system} value={system}>{system}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Approach</InputLabel>
              <Select
                value={editingData.approach}
                onChange={(e) => handleEditingDataChange('approach', e.target.value)}
                label="Approach"
              >
                {approaches.map(approach => (
                  <MenuItem key={approach} value={approach}>{approach}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <EquipmentMultiSelect
              facilityId={defaultFacilityId}
              value={editingData.equipmentNeeded || []}
              onChange={(selectedIds) => handleEditingDataChange('equipmentNeeded', selectedIds)}
              fullWidth
              label="Equipment Needed"
            />
            <TextField
              label="Default Duration (minutes)"
              type="number"
              value={editingData.defaultDuration}
              onChange={(e) => handleEditingDataChange('defaultDuration', e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateNew} variant="contained">
            Create Mapping
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurgicalProcedureMapping; 